import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  DpProcessListItem,
  listProcessesApi,
  openAdmissionApi,
  PROCESS_KIND_LABELS,
  PROCESS_STATUS_BADGE,
  PROCESS_STATUS_LABELS,
} from "../api/processesApi";
import type { DpEmployeeDetail } from "../api/employeesApi";
import { formatDateOnly } from "../utils/format";
import { DP_PROCESS_PAGE } from "../routes";
import TerminationFormModal from "./TerminationFormModal";

type Props = { token: string; employee: DpEmployeeDetail; onChanged: () => void };

/** Aba "Admissão e desligamento" da ficha: processos do colaborador e os botões de abertura. */
export default function EmployeeProcessesTab({ token, employee, onChanged }: Props) {
  const navigate = useNavigate();
  const [items, setItems] = useState<DpProcessListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [terminationOpen, setTerminationOpen] = useState(false);

  /** Só o contrato ativo abre processo — rescindido não admite nenhum dos dois. */
  const activeContract = useMemo(() => employee.contracts.find((c) => !c.termination) ?? null, [employee.contracts]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const page = await listProcessesApi(token, { employeeId: employee.id, limit: 100 });
      setItems(page.items);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os processos.");
    } finally {
      setLoading(false);
    }
  }, [token, employee.id]);

  useEffect(() => {
    load();
  }, [load]);

  const hasOpen = (kind: DpProcessListItem["kind"]) =>
    items.some((p) => p.kind === kind && p.status === "EM_ANDAMENTO");

  const startAdmission = async () => {
    if (!activeContract) return;
    setBusy(true);
    try {
      const process = await openAdmissionApi(token, employee.id, { contractId: activeContract.id });
      toast.success("Checklist de admissão iniciado.");
      navigate(DP_PROCESS_PAGE(process.id));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao iniciar a admissão.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="dpSection">
      <div className="dpHeader" style={{ marginBottom: 12 }}>
        <h2 className="dpSection__title" style={{ margin: 0 }}>
          Admissão e desligamento
        </h2>
        <div className="dpHeader__actions">
          <button
            className="dpBtn dpBtn--primary"
            disabled={busy || !activeContract || hasOpen("ADMISSAO")}
            onClick={startAdmission}
          >
            Iniciar admissão
          </button>
          <button
            className="dpBtn dpBtn--danger"
            disabled={busy || !activeContract || hasOpen("DESLIGAMENTO")}
            onClick={() => setTerminationOpen(true)}
          >
            Iniciar desligamento
          </button>
        </div>
      </div>

      {!activeContract ? (
        <p className="dpNotice">
          Sem contrato ativo: registre a admissão na aba Contrato antes de abrir um checklist.
        </p>
      ) : null}

      <div className="dpTableWrapper">
        <table className="dpTable">
          <thead>
            <tr>
              <th>Processo</th>
              <th>Data-base</th>
              <th>Etapas</th>
              <th>Situação</th>
              <th style={{ width: 100 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="dpEmpty">
                  Carregando...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5} className="dpEmpty">
                  Nenhum processo aberto para este colaborador.
                </td>
              </tr>
            ) : (
              items.map((p) => (
                <tr key={p.id}>
                  <td>
                    <strong>{PROCESS_KIND_LABELS[p.kind]}</strong>
                    <div style={{ fontSize: 12, color: "var(--eco-muted)" }}>{p.template?.name ?? "—"}</div>
                  </td>
                  <td>{formatDateOnly(p.baseDate)}</td>
                  <td>
                    {p.totalSteps - p.pendingSteps}/{p.totalSteps}
                    {p.overdueSteps > 0 ? (
                      <span className="dpBadge dpBadge--red" style={{ marginLeft: 6 }}>
                        {p.overdueSteps} atrasada(s)
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <span className={`dpBadge ${PROCESS_STATUS_BADGE[p.status]}`}>{PROCESS_STATUS_LABELS[p.status]}</span>
                  </td>
                  <td>
                    <button
                      className="dpBtn dpBtn--secondary"
                      style={{ height: 30, padding: "0 10px" }}
                      onClick={() => navigate(DP_PROCESS_PAGE(p.id))}
                    >
                      Abrir
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {terminationOpen && activeContract ? (
        <TerminationFormModal
          token={token}
          employeeId={employee.id}
          contract={activeContract}
          onClose={() => setTerminationOpen(false)}
          onOpened={(processId) => {
            setTerminationOpen(false);
            onChanged();
            navigate(DP_PROCESS_PAGE(processId));
          }}
        />
      ) : null}
    </section>
  );
}
